// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Shield } from 'lucide-react';

const cards = [
  {
    id: 'rc-auto',
    title: 'RC Auto',
    subtitle: 'Obbligatoria per legge — Responsabilità Civile',
    color: '#5B8DB8',
    icon: '🚗',
    content: [
      { label: 'Copertura minima obbligatoria', value: '€6,45M per danni a persone; €1,22M per danni a cose' },
      { label: 'Massimale consigliato', value: '€10M per danni a persone (aumenta la protezione patrimoniale)' },
      { label: 'Franchigia tipica', value: '€0 — la RC Auto è risarcita senza franchigia dal tuo assicuratore' },
      { label: 'Costo medio annuo', value: '€400-€900 per auto (varia per zona, età, bonus-malus)' },
      { label: 'Cosa copre', value: 'Danni fisici e materiali che causi a terzi con il tuo veicolo' },
      { label: 'Cosa NON copre', value: 'Danni al tuo veicolo, furto, eventi atmosferici — serve Kasko o polizza accessorie' },
    ],
    tip: 'Abbina sempre una polizza Kasko o atti vandalici se l\'auto ha meno di 5 anni o un valore > €15.000.',
  },
  {
    id: 'salute',
    title: 'Assicurazione Salute',
    subtitle: 'Sanità integrativa — rimborso spese mediche',
    color: '#3D7A52',
    icon: '🏥',
    content: [
      { label: 'Tipologie principali', value: 'Rimborso spese (open policy) | Convenzioni dirette | Indennità giornaliera ricovero' },
      { label: 'Massimale tipico', value: '€100.000–€500.000 per anno assicurativo' },
      { label: 'Franchigia comune', value: '€250–€500 per sinistro (le polizze con franchigia alta costano meno)' },
      { label: 'Costo medio annuo', value: '€600–€2.000 per adulto (varia per età e coperture scelte)' },
      { label: 'Cosa copre', value: 'Visite specialistiche, diagnostica, ricoveri, pronto soccorso in strutture private, odontoiatria (opzione)' },
      { label: 'Cosa NON copre (di solito)', value: 'Cure estetiche, medicina alternativa, preesistenze non dichiarate, primo anno per alcune malattie' },
    ],
    tip: 'Se hai accesso a un fondo sanitario integrativo tramite il lavoro (es. MetaSalute, Fondo Est), sfruttalo prima: costa meno di una polizza individuale.',
  },
  {
    id: 'casa',
    title: 'Assicurazione Casa',
    subtitle: 'Incendio, furto, danni al fabbricato e contenuto',
    color: '#C9A84C',
    icon: '🏠',
    content: [
      { label: 'Coperture base', value: 'Incendio & fulmini | Esplosione | Danni da acqua (tubature) | Furto e rapina' },
      { label: 'Coperture avanzate', value: 'Terremoto & alluvione (spesso escluse di default) | Guasto apparecchi elettrici | Responsabilità civile condominiale' },
      { label: 'Somma assicurata fabbricato', value: 'Valore di ricostruzione a nuovo (≠ valore di mercato!) — calcola €1.200–€1.600/m²' },
      { label: 'Somma assicurata contenuto', value: 'Inventario oggetti casa: mobili, elettrodomestici, gioielli (limite specifico)' },
      { label: 'Costo medio annuo', value: '€200–€600 per un appartamento standard (dipende da m², zona sismica, coperture)' },
      { label: 'Errore comune', value: 'Sotto-assicurazione: se il valore assicurato è < valore reale, il risarcimento si riduce proporzionalmente (art. 1907 c.c.)' },
    ],
    tip: 'Includi sempre la Responsabilità Civile Famiglia (RCF) nella polizza casa: copre i danni che tu o i tuoi familiari causate a terzi (es. allagamento al vicino di sotto).',
  },
  {
    id: 'reddito',
    title: 'Assicurazione Reddito / Invalidità',
    subtitle: 'Protezione del reddito in caso di inabilità lavorativa',
    color: '#9B7DD4',
    icon: '💼',
    content: [
      { label: 'Tipi di polizza', value: 'Invalidità Permanente da Infortunio (IPI) | Invalidità Permanente da Malattia (IPM) | Temporanea da inabilità' },
      { label: 'Soglia di attivazione', value: 'Di solito il 25–35% di invalidità permanente; alcune polizze partono dal 5%' },
      { label: 'Rendita o capitale?', value: 'Rendita mensile (per mantenere il tenore di vita) vs capitale una-tantum — la rendita è più indicata per invalidità grave' },
      { label: 'Chi ne ha più bisogno', value: 'Liberi professionisti e autonomi (no tutela INPS adeguata) | Chi ha mutuo in corso | Famiglie monoreddito' },
      { label: 'Costo medio annuo', value: '€300–€1.500 (dipende da età, professione, grado di copertura)' },
      { label: 'INPS non basta', value: 'L\'assegno di invalidità INPS scatta solo dal 67% di riduzione della capacità lavorativa — molte invalidità parziali non sono coperte' },
    ],
    tip: 'Per i professionisti con cassa previdenziale (avvocati, medici, ingegneri), verifica prima la copertura della tua cassa: spesso offre già un indennizzo base per invalidità.',
  },
  {
    id: 'cyber',
    title: 'Cyber Insurance',
    subtitle: 'Protezione da attacchi informatici e furto di identità',
    color: '#E84040',
    icon: '🔒',
    content: [
      { label: 'Cosa copre (privati)', value: 'Furto di identità digitale | Accesso non autorizzato a conti bancari | Cyberbullismo | Ransomware su dispositivi personali' },
      { label: 'Cosa copre (aziende)', value: 'Violazione dati (GDPR breach) | Business interruption | Cyber-estorsione | Costi notifica agli interessati' },
      { label: 'Massimale tipico (privati)', value: '€5.000–€25.000 per anno' },
      { label: 'Costo medio annuo', value: '€50–€200 per privati (spesso inclusa in polizze casa o banca); €2.000–€20.000 per PMI' },
      { label: 'Servizi accessori inclusi', value: 'Assistenza legale, monitoraggio dark web, ripristino identità, supporto tecnico 24/7' },
      { label: 'Crescita del rischio', value: 'Phishing +78% nel 2023. Furti credenziali bancarie: 3,1M casi in Italia (2023). Il rischio cyber è reale anche per i privati.' },
    ],
    tip: 'Controlla se la tua banca già include una base di tutela cyber (alcune lo fanno per conti premium). Se usi crypto o hai dati aziendali in casa, una polizza dedicata vale l\'investimento.',
  },
];

const CoperturaVitaCalc = () => {
  const [open, setOpen] = useState(null);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="copertura-vita-calc">
      <div className="mb-6 p-4 rounded-xl flex items-start gap-3" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)' }}>
        <Shield className="w-5 h-5 text-[#C9A84C] mt-0.5 flex-shrink-0" />
        <p className="text-sm text-[#888780] leading-relaxed">
          La protezione assicurativa è la base di qualsiasi piano finanziario solido: prima di investire, assicurati di avere le coperture giuste. Clicca su ogni scheda per approfondire costi, coperture e consigli pratici.
        </p>
      </div>

      <div className="space-y-3">
        {cards.map((card) => (
          <div key={card.id} className="rounded-xl overflow-hidden" style={{ border: `1px solid ${open === card.id ? card.color + '60' : 'rgba(255,255,255,0.08)'}` }}
            data-testid={`card-${card.id}`}>
            <button
              onClick={() => setOpen(open === card.id ? null : card.id)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-white/[0.02] transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{card.icon}</span>
                <div>
                  <p className="font-semibold text-[#E8E8E4] text-sm">{card.title}</p>
                  <p className="text-xs text-[#888780] mt-0.5">{card.subtitle}</p>
                </div>
              </div>
              <ChevronDown className={`w-4 h-4 flex-shrink-0 ml-4 transition-transform text-[#888780] ${open === card.id ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {open === card.id && (
                <motion.div
                  initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
                  className="overflow-hidden"
                  style={{ borderTop: `1px solid ${card.color}30` }}
                >
                  <div className="p-4 space-y-3">
                    <div className="grid sm:grid-cols-2 gap-3">
                      {card.content.map((item, i) => (
                        <div key={i} className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                          <p className="text-xs font-medium mb-1" style={{ color: card.color }}>{item.label}</p>
                          <p className="text-xs text-[#888780] leading-relaxed">{item.value}</p>
                        </div>
                      ))}
                    </div>
                    <div className="p-3 rounded-lg text-xs text-[#888780] leading-relaxed" style={{ background: `${card.color}15`, border: `1px solid ${card.color}30` }}>
                      <strong style={{ color: card.color }}>Consiglio pratico: </strong>{card.tip}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
</motion.div>
  );
};

export default CoperturaVitaCalc;
